// reCAPTCHA v3 verification helper. Calls the recaptcha-verify Edge
// Function and returns a verdict the caller can switch on. The score is
// NEVER exposed to the browser (decision PO 2026-05-03) — only success /
// failure code.
//
// Refs: Spec V1.1 §M5 L840-842, §M13.6 · Migration 20260503100000 ·
//       Edge Function supabase/functions/recaptcha-verify/index.ts

import { env } from './env';

export type RecaptchaAction =
  | 'signin'
  | 'admin_signin'
  | 'signup'
  | 'reset_request'
  | 'contact'
  | 'interest'
  | 'callback'
  | 'seller';

export type RecaptchaVerdict =
  | { success: true }
  | { success: false; code: 'low_score' | 'rate_limited' | 'verify_failed' };

type VerifyInput = {
  token: string;
  action: RecaptchaAction;
  email?: string;
};

export async function verifyRecaptcha(input: VerifyInput): Promise<RecaptchaVerdict> {
  // Dev bypass — when the site key is absent, the Provider is not mounted
  // and `useRecaptchaToken()` returns null tokens. We short-circuit here
  // so the caller treats the form as if reCAPTCHA passed. The prod guard
  // in env.ts boot-crashes when the key is empty in production, so this
  // branch is unreachable outside dev.
  if (!env.recaptchaSiteKey) {
    if (env.isDev) {
      console.warn('[recaptcha] VITE_RECAPTCHA_SITE_KEY empty — skipping verification (dev only).');
    }
    return { success: true };
  }

  if (!env.supabase.url || !env.supabase.anonKey) {
    return { success: false, code: 'verify_failed' };
  }

  let resp: Response;
  try {
    resp = await fetch(`${env.supabase.url}/functions/v1/recaptcha-verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: env.supabase.anonKey,
        Authorization: `Bearer ${env.supabase.anonKey}`,
      },
      body: JSON.stringify({
        token: input.token,
        action: input.action,
        email: input.email,
      }),
    });
  } catch {
    return { success: false, code: 'verify_failed' };
  }

  if (resp.status === 429) {
    return { success: false, code: 'rate_limited' };
  }
  if (!resp.ok) {
    return { success: false, code: 'verify_failed' };
  }

  const body = (await resp.json().catch(() => null)) as RecaptchaVerdict | null;
  if (!body || typeof body.success !== 'boolean') {
    return { success: false, code: 'verify_failed' };
  }
  return body;
}
